import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState("request");

  const handleRequest = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.token) setToken(data.token);
      setStep("reset");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: newPassword });
      setSuccess(data.message);
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed");
    }
    setLoading(false);
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-2">
            {step === "done" ? "PASSWORD RESET" : "FORGOT PASSWORD"}
          </h1>
          <p className="text-[#A3A3A3] text-sm">
            {step === "request" && "Enter your email to receive a reset token."}
            {step === "reset" && "Enter the reset token and your new password."}
            {step === "done" && "Your password has been updated."}
          </p>
        </div>

        <div className="card-dark p-8 space-y-5">
          {step === "request" && (
            <form onSubmit={handleRequest} className="space-y-5" data-testid="forgot-form">
              <div>
                <label className="overline block mb-2">EMAIL ADDRESS</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark"
                  placeholder="Enter your email"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-2 rounded-sm" data-testid="forgot-error">{error}</div>}
              <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="forgot-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {loading ? "SENDING..." : "SEND RESET TOKEN"}
              </button>
            </form>
          )}

          {step === "reset" && (
            <form onSubmit={handleReset} className="space-y-5" data-testid="reset-form">
              {sent && (
                <div className="text-[#D4AF37] text-sm bg-[#D4AF37]/10 border border-[#D4AF37]/20 px-4 py-2 rounded-sm" data-testid="reset-info">
                  A reset token has been sent to your email. Check your inbox (and spam folder).
                </div>
              )}
              <div>
                <label className="overline block mb-2">RESET TOKEN</label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="input-dark"
                  placeholder="Paste your reset token"
                  required
                  data-testid="reset-token-input"
                />
              </div>
              <div>
                <label className="overline block mb-2">NEW PASSWORD</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-dark"
                  placeholder="Enter new password (min 6 chars)"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
              </div>
              {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-2 rounded-sm" data-testid="reset-error">{error}</div>}
              <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="reset-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? "RESETTING..." : "RESET PASSWORD"}
              </button>
            </form>
          )}

          {step === "done" && (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="text-green-400 text-sm bg-green-400/10 border border-green-400/20 px-4 py-3 rounded-sm">{success}</div>
              <Link to="/login" className="btn-gold inline-block px-6 py-2" data-testid="back-to-login">SIGN IN</Link>
            </div>
          )}

          {step !== "done" && (
            <div className="text-center text-sm text-[#A3A3A3]">
              <Link to="/login" className="text-[#D4AF37] hover:underline flex items-center justify-center gap-1" data-testid="back-link">
                <ArrowLeft size={14} /> Back to Sign In
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
